import { useEffect, type ReactNode } from 'react';
import { usePresence } from '../../lib/use-presence.js';

// Matches the CSS: --dur-modal is 280ms; exit is 0.7× → ~196ms. We keep
// the node mounted for the full enter duration on the exit side to be
// safe (overshoot unmount is invisible, premature unmount flashes).
const MODAL_EXIT_MS = 280;

/** Width tiers for the modal shell. `lg` (default) preserves the original
 *  max-w-lg; wider tiers are for content-rich modals (e.g. the reserve picker
 *  master-detail). */
const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-6xl',
} as const;

export function Dialog({
  open,
  onClose,
  title,
  children,
  size = 'lg',
  className,
  bodyClassName,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Width tier — defaults to `lg` (unchanged from before). */
  size?: keyof typeof SIZE_CLASS;
  /** Extra classes on the modal shell (e.g. height caps, padding overrides). */
  className?: string;
  /** Replaces the default `p-6` body padding when set (e.g. flush layouts). */
  bodyClassName?: string;
}) {
  const { mounted, phase } = usePresence(open, MODAL_EXIT_MS);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;
  const cls = phase === 'enter' ? 'is-enter' : 'is-exit';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        className={`hestia-backdrop ${cls} absolute inset-0 bg-ink/40`}
        onClick={onClose}
        aria-label="Close dialog"
      />
      <div
        className={`hestia-modal ${cls} relative ${size === 'lg' ? '' : 'w-full'} ${SIZE_CLASS[size]} mx-4 rounded-xl bg-[var(--app-surface)] shadow-xl ${bodyClassName ?? 'p-6'} ${className ?? ''}`}
      >
        {title && <h2 className="mb-4 text-h2 font-semibold text-[var(--ink-700)]">{title}</h2>}
        {children}
      </div>
    </div>
  );
}
